import classNames from 'classnames';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { useMediaQuery } from 'react-responsive';

import Button, { TYPES } from 'components/Button';
import Image from 'components/ImageV2';
import { IMAGE_WIDTHS } from 'constants/images';
import { extraLarge, largeUp, medium } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import GENERIC_CTA_CLICKED_EVENT from 'utils/Rudderstack/constants';

const StoryCardV2 = ({
  backgroundColor,
  ctaLinks,
  desktopImage,
  dyCampaignData,
  internalName,
  gradientEnabled,
  heading,
  headerEyebrow,
  mobileImage = mobileImage || desktopImage,
  subheader,
  trackCtaLinkClick,
}) => {
  const hasCTALinks = ctaLinks.length && ctaLinks.length > 0;
  const elementRef = useRef(null);
  const isLargeUp = useMediaQuery({ query: largeUp });

  const image = isLargeUp ? desktopImage : mobileImage;
  const imageWidth = elementRef?.current?.clientWidth * 2 || IMAGE_WIDTHS.W_800;

  return (
    <div className="StoryCardV2" ref={elementRef}>
      <Image
        className="StoryCardV2__image"
        src={image?.file?.url}
        width={imageWidth}
        coverParentNode
        transformations={{
          width: imageWidth,
        }}
      />

      <div className="StoryCardV2__content">
        {headerEyebrow && <span className="StoryCardV2__header-eyebrow">{headerEyebrow}</span>}
        {heading && (
          <span
            className={classNames('StoryCardV2__heading', {
              'StoryCardV2__heading--large': !hasCTALinks,
            })}
          >
            {heading}
          </span>
        )}
        {subheader && <p className="StoryCardV2__subheader">{subheader}</p>}

        {hasCTALinks ? (
          <div
            className={classNames('StoryCardV2__cta-links', {
              'StoryCardV2__cta-links--single': ctaLinks.length === 1,
            })}
          >
            {ctaLinks.map((ctaLink, index) => (
              <Button
                key={`cta-link-${index}-${ctaLink.label}`}
                className="StoryCardV2__cta-link"
                type={TYPES.INVERSE}
                linkTo={ctaLink.url}
                target={ctaLink.openInNewWindow ? '_blank' : '_self'}
                onClick={() => {
                  trackCtaLinkClick({
                    type: GENERIC_CTA_CLICKED_EVENT.TYPES.BUTTON,
                    location: GENERIC_CTA_CLICKED_EVENT.LOCATIONS.STORY_CARD_V2,
                    name: dyCampaignData?.experienceName || internalName,
                    url: ctaLink.url,
                    campaignName: !isEmpty(dyCampaignData)
                      ? GENERIC_CTA_CLICKED_EVENT.CAMPAIGNS.COLLECTIONS_CLICKABLE_STORY_CARD
                      : '',
                    variationId: dyCampaignData?.variationId || 0,
                    variationName: dyCampaignData?.variationName || '',
                    decisionId: dyCampaignData?.decisionId || '',
                  });
                }}
              >
                {ctaLink.label}
              </Button>
            ))}
          </div>
        ) : null}
      </div>
      <style jsx>{`
        .StoryCardV2 {
          background-color: ${backgroundColor};
          position: relative;
          width: 100%;
          height: 100%;
          min-height: 315px;
        }

        .StoryCardV2__content::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          opacity: ${gradientEnabled ? 1 : 0};
          background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 50%);
        }

        .StoryCardV2__content {
          margin-top: auto;
          height: fit-content;
          position: absolute;
          inset: 0;
          color: ${Theme.get('colors-white')};
          padding: ${Theme.get('spacing') * 3}px;
          gap: ${Theme.get('spacing')}px;
        }

        .StoryCardV2__subheader {
          position: relative;
          line-height: ${Theme.get('letter-spacing-extra-large')};
          font-size: ${!hasCTALinks
            ? Theme.get('font-size-px')
            : Theme.get('font-size-description-px')};
          max-width: 420px;
          font-weight: ${Theme.get('font-weight-normal')};
          margin-bottom: ${!hasCTALinks ? `${Theme.get('spacing') * 5}px` : 0};
        }

        .StoryCardV2__cta-links {
          margin-top: ${Theme.get('spacing') * 2}px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: ${Theme.get('spacing')}px;
        }

        @media ${medium} {
          .StoryCardV2 {
            min-height: 50vw;
          }
        }

        @media ${largeUp} {
          .StoryCardV2 {
            text-align: center;
            min-height: auto;
          }
          .StoryCardV2__cta-links {
            grid-template-columns: 1fr;
            flex-wrap: wrap;
          }
        }

        @media ${extraLarge} {
          .StoryCardV2__cta-links {
            grid-template-columns: 1fr 1fr;
          }

          .StoryCardV2__cta-links.StoryCardV2__cta-links--single {
            grid-template-columns: 1fr;
          }

          .StoryCardV2__subheader {
            font-size: ${Theme.get('font-size-px')};
          }
        }
      `}</style>

      <style jsx global>{`
        .StoryCardV2__image {
          object-fit: cover;
          position: absolute;
          inset: 0;
        }

        .StoryCardV2__cta-links .Button {
          margin: 0;
          flex-grow: 1;
        }

        .StoryCardV2__header-eyebrow {
          display: block;
          position: relative;
          letter-spacing: ${Theme.get('letter-spacing-large-px')};
          font-weight: 700;
          font-size: ${Theme.get('font-size-small-px')};
          text-transform: uppercase;
          line-height: 1.3;
          margin-bottom: ${Theme.get('spacing-px')};
        }

        .StoryCardV2__heading {
          display: block;
          font-size: ${Theme.get('font-size-large-px')};
          font-weight: ${Theme.get('font-weight-bold')};
          letter-spacing: 0.5px;
          line-height: 1.5;
          position: relative;
          margin-bottom: ${Theme.get('spacing-px')};
          color: inherit;
        }

        .StoryCardV2__heading.StoryCardV2__heading--large {
          font-size: 20px;
        }

        .StoryCardV2__cta-links a.StoryCardV2__cta-link {
          padding: ${Theme.get('spacing-px')} 0;
          line-height: 1.75;
          font-size: ${Theme.get('font-size-small-px')};
          white-space: nowrap;
        }

        .StoryCardV2__cta-links--single a.StoryCardV2__cta-link {
          max-width: 270px;
        }

        @media ${largeUp} {
          .StoryCardV2__cta-links a.StoryCardV2__cta-link {
            white-space: nowrap;
          }

          .StoryCardV2__heading {
            font-size: 22px;
            line-height: 1.32;
          }
        }

        @media ${extraLarge} {
          .StoryCardV2__cta-links a.StoryCardV2__cta-link {
            font-size: ${Theme.get('font-size-description-px')};
            max-width: none;
          }
        }
      `}</style>
    </div>
  );
};

StoryCardV2.propTypes = {
  backgroundColor: PropTypes.string,
  ctaLinks: PropTypes.oneOfType([
    PropTypes.arrayOf({
      label: PropTypes.string,
      openInNewWindow: PropTypes.bool,
      url: PropTypes.string,
    }),
    PropTypes.array,
  ]),
  desktopImage: PropTypes.shape({
    file: PropTypes.shape({
      url: PropTypes.string,
    }),
    description: PropTypes.string,
  }),
  dyCampaignData: PropTypes.shape({
    experienceName: PropTypes.string,
    experienceId: PropTypes.number,
    variationId: PropTypes.number,
    variationName: PropTypes.string,
    campaign_name: PropTypes.string,
    decisionId: PropTypes.string,
  }),
  internalName: PropTypes.string.isRequired,
  gradientEnabled: PropTypes.bool,
  heading: PropTypes.string,
  headerEyebrow: PropTypes.string,
  mobileImage: PropTypes.shape({
    file: PropTypes.shape({
      url: PropTypes.string,
    }),
    description: PropTypes.string,
  }),
  subheader: PropTypes.string,
  trackCtaLinkClick: PropTypes.func.isRequired,
};

StoryCardV2.defaultProps = {
  backgroundColor: '',
  ctaLinks: [],
  desktopImage: {},
  dyCampaignData: {},
  gradientEnabled: false,
  heading: '',
  headerEyebrow: '',
  mobileImage: {},
  subheader: '',
};

export default StoryCardV2;
